<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import type { ICatalogEditorTarget } from '@/composition/catalog/catalog-page-state';
  import {
    provideCategoryEditor,
    useCategoryEditor,
  } from '@/composition/catalog/use-category-editor';
  import { isAvailableHere } from '@/domain/catalog/availability';
  import type { ICatalogMenuEntry } from '@/domain/catalog/catalog-menus';
  import V2CatalogEditorBanner from '../shared/V2CatalogEditorBanner.vue';
  import V2CatalogEditorFooter from '../shared/V2CatalogEditorFooter.vue';
  import V2CatalogEditorHeader from '../shared/V2CatalogEditorHeader.vue';
  import V2CatalogCategoryBasicsSection from './V2CatalogCategoryBasicsSection.vue';
  import V2CatalogCategoryMoreSection from './V2CatalogCategoryMoreSection.vue';
  import V2CatalogCategoryPrintingSection from './V2CatalogCategoryPrintingSection.vue';
  import V2CatalogCategoryWhereSection from './V2CatalogCategoryWhereSection.vue';

  /**
   * The category editor (plan §5.15): «Lo básico», «¿Dónde se ve?»,
   * «Impresión» and «Más ajustes», with the product editor's header,
   * notices and footer. One instance per target (the host keys it).
   */
  const props = defineProps<{
    target: ICatalogEditorTarget;
    /** Docked beside the list (the header shows its own ✕). */
    docked: boolean;
  }>();

  const emit = defineEmits<{ (e: 'close'): void }>();

  type TCategoryEditorAction = 'addProduct' | 'delete';

  const editor = useCategoryEditor(props.target);
  provideCategoryEditor(editor);

  const { data, access, online, commands } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const form = computed(() => editor.form.value);
  const category = computed(() => editor.category.value);

  const title = computed(
    () =>
      form.value?.name.trim() ||
      category.value?.name ||
      t('menuPage.editor.newCategory')
  );

  /** «6 productos · 1 no disponible». */
  const subtitle = computed(() => {
    const id = editor.categoryId;
    if (editor.isNew || id === null)
      return t('menuPage.editor.newCategoryNote');
    const items = data.items.value.filter((item) => item.categoryId === id);
    const branchId = data.branchId.value;
    const unavailable = items.filter((item) => !isAvailableHere(item, branchId))
      .length;
    const parts = [
      tc('menuPage.card.products', items.length, { n: items.length }),
    ];
    if (unavailable)
      parts.push(
        tc('menuPage.card.unavailable', unavailable, { n: unavailable })
      );
    return parts.join(' · ');
  });

  const entries = computed<ICatalogMenuEntry<TCategoryEditorAction>[]>(() => {
    if (editor.isNew || !category.value || editor.deleted.value) return [];
    if (!access.value.canManage) return [];
    return [
      {
        key: 'addProduct',
        labelKey: 'menuPage.categoryMenu.addProduct',
        icon: 'plus',
        danger: false,
        write: false,
        plan: null,
      },
      {
        key: 'delete',
        labelKey: 'menuPage.categoryMenu.delete',
        icon: 'delete-outline',
        danger: true,
        write: true,
        plan: null,
      },
    ];
  });

  function onAction(key: string) {
    const current = category.value;
    if (!current) return;
    if (key === 'addProduct')
      commands.runCategoryCommand('addProduct', current);
    else if (key === 'delete') commands.runCategoryCommand('delete', current);
  }

  const footerReadOnly = computed(
    () =>
      editor.readOnly.value &&
      !editor.canEditAvailability.value &&
      !access.value.canTranslate
  );

  const failedText = computed(() =>
    editor.failedExtras.value.length
      ? t('menuPage.editor.failed.translations')
      : null
  );
</script>

<template>
  <div class="v2-catalog-category-editor">
    <V2CatalogEditorHeader
      :title="title"
      :subtitle="subtitle"
      :entries="entries"
      :online="online"
      :show-close="docked"
      @select="onAction"
      @close="emit('close')"
    />

    <div class="editor-body">
      <template v-if="form">
        <V2CatalogEditorBanner
          v-if="editor.deleted.value"
          tone="red"
          :text="t('menuPage.editor.categoryDeleted')"
        />
        <V2CatalogEditorBanner
          v-else-if="editor.remoteChanged.value"
          tone="amber"
          :text="t('menuPage.editor.categoryRemoteChanged')"
        />
        <V2CatalogEditorBanner
          v-if="failedText"
          tone="red"
          :text="failedText"
          :action-text="t('menuPage.retry')"
          :action-disabled="editor.saving.value || !online"
          @action="editor.retryExtras()"
        />

        <V2CatalogCategoryBasicsSection />
        <V2CatalogCategoryWhereSection />
        <V2CatalogCategoryPrintingSection />
        <V2CatalogCategoryMoreSection />
      </template>

      <div v-else class="editor-loading" aria-hidden="true">
        <PosUiSkeleton class="loading-card is-tall" />
        <PosUiSkeleton class="loading-card" />
      </div>
    </div>

    <V2CatalogEditorFooter
      :change-count="editor.changeCount.value"
      :saving="editor.saving.value"
      :saved-flash="editor.savedFlash.value"
      :read-only="footerReadOnly"
      :create="editor.isNew"
      :create-text="t('menuPage.editor.createCategory')"
      :create-another="false"
      :retry="!!failedText && !editor.dirty.value"
      :online="online"
      :save-disabled="editor.deleted.value || !form"
      @close="emit('close')"
      @discard="editor.discard()"
      @save="editor.save()"
      @retry="editor.retryExtras()"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-category-editor {
    @apply tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-bg-canvas;

    .editor-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-3;
      overscroll-behavior-y: contain;
    }

    .editor-loading {
      .loading-card {
        @apply tw-mb-3 tw-h-32 tw-w-full tw-rounded-xl;

        &.is-tall {
          @apply tw-h-48;
        }
      }
    }
  }
</style>
